<template>
    <div class="flex flex-1 flex-col text-white overflow-hidden w-full">
        <SegmentsSettingsBar :settings="settings" />
        <div class="flex-1 flex overflow-hidden">
            <!-- RACE MODE -->
            <template v-if="!isPointsMode">
                <template v-if="players.length <= 2">
                    <component
                        v-for="player in players"
                        :is="players.length === 1 ? 'SegmentsGridPlayer' : 'SegmentsVerticalPlayer'"
                        :key="player.id"
                        :player="player"
                        :is-active="player.id === currentPlayer?.id"
                        :segments="segmentsFor(player)"
                        :projection="projectionFor(player)"
                        :hits-per-target="hitsPerTarget"
                        :darts-submitted="dartsSubmittedFor(player)"
                        :entered-darts="player.id === currentPlayer?.id ? enteredDarts : []"
                        class="flex-1 min-w-0"
                    />
                </template>
                <template v-else>
                    <div class="flex flex-1 flex-col min-h-0 overflow-hidden">
                        <SegmentsGridPlayer
                            :key="currentPlayer?.id"
                            :player="currentPlayer"
                            :is-active="true"
                            :segments="segmentsFor(currentPlayer)"
                            :projection="projectionFor(currentPlayer)"
                            :hits-per-target="hitsPerTarget"
                            :darts-submitted="dartsSubmittedFor(currentPlayer)"
                            :entered-darts="enteredDarts"
                            class="flex-1 min-h-0"
                        />
                        <div
                            v-if="upcomingPlayers.length"
                            class="grid gap-px bg-zinc-800"
                            :style="{ gridTemplateColumns: `repeat(${upcomingPlayers.length}, minmax(0, 1fr))` }"
                        >
                            <SegmentsUpcomingPlayer
                                v-for="(player, index) in upcomingPlayers"
                                :key="player.id"
                                :player="player"
                                :is-next="index === 0"
                                :segments="segmentsFor(player)"
                                :projection="projectionFor(player)"
                            />
                        </div>
                    </div>
                </template>
            </template>
            <!-- POINTS MODE -->
            <template v-else>
                <template v-if="players.length == 1">
                    <PointsVerticalPlayer
                        v-for="player in players"
                        :key="player.id"
                        :player="player"
                        :is-active="player.id === currentPlayer?.id"
                        :score="playerScore(player)"
                        :current-round="currentRoundFor(player)"
                        :total-rounds="totalRounds"
                        :ceiling="ceiling"
                        :target-segment="targetSegmentFor(player)"
                        :next-segment="nextSegmentFor(player, 1)"
                        :third-segment="nextSegmentFor(player, 2)"
                        :darts="playerDarts(player)"
                        class="flex-1 min-w-0"
                    />
                </template>
                <div class="flex flex-col flex-1 min-h-0" v-else-if="players.length == 2">
                    <div class="grid grid-cols-2 grid-rows-1 items-center p-5 flex-1 min-h-0 overflow-hidden">
                        <SegmentsCurrentTargetText
                            :target-segment="targetSegmentFor(currentPlayer)"
                            :number-only="true"
                            :show-next="false"
                            :current-round="currentRoundFor(currentPlayer)"
                            :total-rounds="totalRounds"
                        />
                        <SegmentsDartboard
                        :active-segment="targetSegmentFor(currentPlayer)"
                        :next-segment="nextSegmentFor(currentPlayer, 1)"
                        :third-segment="nextSegmentFor(currentPlayer, 2)"
                        :show-full-segment="true"
                        class="min-h-0 h-full w-full block"
                        />
                    </div>
                    <div class="grid grid-cols-2">
                        <SegmentsDualPlayer 
                            v-for="player in players"
                            :key="player.id"
                            :player="player"
                            :is-active="player.id === currentPlayer?.id"
                            :score="playerScore(player)"
                            :current-round="currentRoundFor(player)"
                            :total-rounds="totalRounds"
                            :ceiling="ceiling"
                            :target-segment="targetSegmentFor(player)"
                            :next-segment="nextSegmentFor(player, 1)"
                            :third-segment="nextSegmentFor(player, 2)"
                            :darts="playerDarts(player)"
                        />
                    </div>
                </div>
                <template v-else>
                    <div class="flex flex-1 flex-col min-h-0 overflow-hidden">
                        <PointsGridPlayer
                            :key="currentPlayer?.id"
                            :player="currentPlayer"
                            :is-active="true"
                            :score="playerScore(currentPlayer)"
                            :current-round="currentRoundFor(currentPlayer)"
                            :total-rounds="totalRounds"
                            :ceiling="ceiling"
                            :target-segment="targetSegmentFor(currentPlayer)"
                            :next-segment="nextSegmentFor(currentPlayer, 1)"
                            :third-segment="nextSegmentFor(currentPlayer, 2)"
                            :darts="playerDarts(currentPlayer)"
                            class="flex-1 min-h-0"
                        />
                        <div
                            v-if="upcomingPlayers.length"
                            class="grid gap-px bg-zinc-800"
                            :style="{ gridTemplateColumns: `repeat(${upcomingPlayers.length}, minmax(0, 1fr))` }"
                        >
                            <PointsUpcomingPlayer
                                v-for="(player, index) in upcomingPlayers"
                                :key="player.id"
                                :player="player"
                                :is-next="index === 0"
                                :points-mode="true"
                                :score="playerScore(player)"
                                :ceiling="ceiling"
                                :current-round="currentRoundFor(player)"
                                :total-rounds="totalRounds"
                                :hide-target="true"
                            />
                        </div>
                    </div>
                </template>
            </template>
        </div>
        <SegmentsKeyboard
            :entered-darts="enteredDarts"
            :score-options="scoreOptions"
            :darts-per-turn="dartsPerTurn"
            :darts-remaining="dartsRemainingThisTurn"
            :is-submitting="isSubmitting"
            :disabled="!currentPlayer || isMatchOver"
            @hit="addHit"
            @miss="addMiss"
            @undo="removeLastDart"
            @submit="submitTurn"
        />
    </div>
</template>

<script>
import { useMatchStore } from '@/stores/match.js'
import { DART_TYPES } from '@/constants/dartTypes.js'
import SegmentsKeyboard from '@/components/games/segments/SegmentsKeyboard.vue'
import SegmentsSettingsBar from '@/components/games/segments/SegmentsSettingsBar.vue'
import SegmentsVerticalPlayer from '@/components/games/segments/layouts/raceMode/SegmentsVerticalPlayer.vue'
import SegmentsGridPlayer from '@/components/games/segments/layouts/raceMode/SegmentsGridPlayer.vue'
import SegmentsUpcomingPlayer from '@/components/games/segments/SegmentsUpcomingPlayer.vue'
import PointsVerticalPlayer from '@/components/games/segments/layouts/pointsMode/SegmentsVerticalPlayer.vue'
import PointsGridPlayer from '@/components/games/segments/layouts/pointsMode/SegmentsGridPlayer.vue'
import PointsUpcomingPlayer from '@/components/games/segments/SegmentsUpcomingPlayer.vue'
import SegmentsDualPlayer from '@/components/games/segments/layouts/pointsMode/SegmentsDualPlayer.vue'
import SegmentsDartboard from '@/components/games/segments/SegmentsDartboard.vue'
import SegmentsCurrentTargetText from '@/components/games/segments/SegmentsCurrentTargetText.vue'

export default {
    name: 'SegmentsTrainingGame',
    components: {
        SegmentsKeyboard,
        SegmentsSettingsBar,
        SegmentsVerticalPlayer,
        SegmentsGridPlayer,
        SegmentsUpcomingPlayer,
        PointsVerticalPlayer,
        PointsGridPlayer,
        PointsUpcomingPlayer,
        SegmentsDualPlayer,
        SegmentsDartboard,
        SegmentsCurrentTargetText,
    },
    emits: ['quit-match'],
    data() {
        return {
            enteredDarts: [],
            isSubmitting: false,
        }
    },
    computed: {
        matchStore() {
            return useMatchStore()
        },
        currentPlayer() {
            return this.matchStore.getCurrentPlayer
        },
        players() {
            return this.matchStore.getProfiles
        },
        // Everyone but the active player, ordered by upcoming turn (wraps around).
        upcomingPlayers() {
            const players = this.players
            const currentIndex = players.findIndex((p) => p.id === this.currentPlayer?.id)
            if (currentIndex === -1) return players
            return [...players.slice(currentIndex + 1), ...players.slice(0, currentIndex)]
        },
        settings() {
            return this.matchStore.getSettings
        },
        hitsPerTarget() {
            return this.settings?.hits_per_target || 1
        },
        dartsPerTurn() {
            return this.settings?.darts_per_turn || 3
        },
        // Darts already thrown in the active player's in-progress turn. A turn stays
        // open until it holds `dartsPerTurn` darts, so a partial submit (e.g. one
        // finishing dart) leaves the turn on this player with darts already in it.
        // Once a turn is full it's complete and the next dart starts a fresh turn,
        // so treat a full count as zero-thrown.
        dartsThrownThisTurn() {
            const thrown = this.playerLastTurn(this.currentPlayer)?.darts?.length || 0
            return thrown >= this.dartsPerTurn ? 0 : thrown
        },
        // How many more darts the active player may enter this turn. Capping entry to
        // this (instead of a flat dartsPerTurn) stops a submission from overflowing
        // the current turn and spilling darts into the next one.
        dartsRemainingThisTurn() {
            return Math.max(0, this.dartsPerTurn - this.dartsThrownThisTurn)
        },
        // Points mode (segment_mode 1) vs Race mode (0).
        isPointsMode() {
            return this.settings?.segment_mode == 1
        },
        // Points sub-mode: 0 = Ceiling, 1 = Rounds.
        pointsModeType() {
            return this.settings?.points_mode || 0
        },
        // The ceiling to fill in Ceiling mode, or null in Rounds mode.
        ceiling() {
            return this.isPointsMode && this.pointsModeType === 0
                ? this.settings?.points_ceiling || 0
                : null
        },
        // Only Rounds mode has a fixed round count. In Ceiling mode there is no
        // round total, so return 1 to keep the "Round x/y" label hidden.
        totalRounds() {
            return this.isPointsMode && this.pointsModeType === 1
                ? this.settings?.rounds || 1
                : 1
        },
        // The canonical, ordered segment list for the current leg with hits zeroed.
        // Only the starting player's turn is seeded server-side, so players who
        // haven't thrown yet have no turn of their own — they fall back to this so
        // their board still shows the first target instead of rendering blank.
        legSegments() {
            const leg = this.matchStore.getCurrentLeg
            const seeded = leg?.turns?.[0]?.segments
            if (!seeded?.length) return []
            return seeded.map((segment) => ({ ...segment, hits: 0 }))
        },
        turnProjection() {
            return this.projectionFor(this.currentPlayer)
        },
        // The segment the active player is throwing at. In Points mode it's the
        // team's current_segment as-is (progression is per round, not per hit);
        // in Race mode it's where the in-progress darts have advanced to.
        activeTarget() {
            if (this.isPointsMode) {
                return this.playerLastTurn(this.currentPlayer)?.current_segment || null
            }
            return this.turnProjection.target
        },
        // Buttons the keyboard renders. Race mode: the single target. Points mode:
        // the ways to score the target number (single/double/triple, or both bulls).
        scoreOptions() {
            const target = this.activeTarget
            if (!target) return []
            if (this.isPointsMode) {
                return this.pointsScoreOptions(target)
            }
            return [
                {
                    label: this.segmentLabel(target),
                    dart_type: target.type,
                    dart_number: this.hitNumberForSegment(target),
                },
            ]
        },
        isMatchOver() {
            return this.matchStore.isMatchOver
        },
    },
    watch: {
        // Clear the in-progress darts only when the turn actually hands to a
        // different player. A successful submit already empties enteredDarts as
        // each dart is confirmed, so keying this off currentSegment instead would
        // also fire on late/duplicate server snapshots (the debounced websocket
        // path applies them after isSubmitting is already false) and wipe the
        // darts the player just entered for their next turn.
        'currentPlayer.id'() {
            if (!this.isSubmitting) {
                this.enteredDarts = []
            }
        },
    },
    methods: {
        // The player's most recent turn in the current leg.
        playerLastTurn(player) {
            const leg = this.matchStore.getCurrentLeg
            if (!player || !leg?.turns?.length) return null
            const playerTurns = leg.turns.filter((turn) => turn.profile_id === player.id)
            return playerTurns[playerTurns.length - 1] || null
        },
        // The player's segment list, falling back to the leg's canonical list for
        // players who haven't thrown yet (only the starter is seeded server-side).
        segmentsFor(player) {
            return this.playerLastTurn(player)?.segments || this.legSegments
        },
        // Where the player is in their segment list. The active player folds in the
        // in-progress darts; everyone else reflects pure server state.
        projectionFor(player) {
            const turn = this.playerLastTurn(player)
            const segments = turn?.segments || this.legSegments
            const currentSegment = turn?.current_segment || segments[0] || null
            const enteredDarts = player?.id === this.currentPlayer?.id ? this.enteredDarts : []
            return this.projectTurn(segments, currentSegment, enteredDarts)
        },
        // Total darts thrown by the player across the current leg.
        dartsSubmittedFor(player) {
            const leg = this.matchStore.getCurrentLeg
            const turns = player ? (leg?.turns || []).filter((t) => t.profile_id === player.id) : []
            return turns.reduce((total, turn) => total + (turn.darts?.length || 0), 0)
        },
        // --- Points mode helpers -------------------------------------------------
        // Server-authoritative score. Points mode deliberately does NOT fold in the
        // in-progress darts: each dart submits separately and returns a fresh
        // snapshot, so a local preview on top would double-count mid-submission.
        // The score simply ticks up as each dart's snapshot arrives.
        playerScore(player) {
            return this.playerLastTurn(player)?.score || 0
        },
        currentRoundFor(player) {
            return this.playerLastTurn(player)?.current_round || 1
        },
        // Index of the team's current_segment within its segment list.
        segmentIndexFor(player) {
            const turn = this.playerLastTurn(player)
            const segments = turn?.segments || []
            const index = segments.findIndex((segment) =>
                this.isSameSegment(segment, turn?.current_segment),
            )
            return index === -1 ? 0 : index
        },
        targetSegmentFor(player) {
            return this.playerLastTurn(player)?.current_segment || null
        },
        // The segment `offset` steps after the current one (for the dartboard's
        // dimmer upcoming highlights).
        nextSegmentFor(player, offset) {
            const segments = this.playerLastTurn(player)?.segments || []
            return segments[this.segmentIndexFor(player) + offset] || null
        },
        // Every dart the player has thrown this leg, for the hit breakdown.
        playerDarts(player) {
            const leg = this.matchStore.getCurrentLeg
            if (!player || !leg?.turns?.length) return []
            return leg.turns
                .filter((turn) => turn.profile_id === player.id)
                .flatMap((turn) => turn.darts || [])
        },
        // The scoring buttons for a Points-mode target, mirroring what the match
        // processor scores: the bullseye only accepts 50, the single bull accepts
        // both bulls, anything else single/double/triple.
        pointsScoreOptions(target) {
            const bullseye = { label: '50', dart_type: DART_TYPES.BULL, dart_number: 50 }
            if (target.type === DART_TYPES.BULL || target.number === 50) {
                return [bullseye]
            }
            if (target.type === DART_TYPES.OUTER_BULL || target.number === 25) {
                return [{ label: '25', dart_type: DART_TYPES.OUTER_BULL, dart_number: 25 }, bullseye]
            }
            return [
                { label: `S${target.number}`, dart_type: DART_TYPES.SINGLE, dart_number: target.number },
                { label: `D${target.number}`, dart_type: DART_TYPES.DOUBLE, dart_number: target.number },
                { label: `T${target.number}`, dart_type: DART_TYPES.TRIPLE, dart_number: target.number },
            ]
        },
        // Replay entered darts over the segment list to find the next target,
        // skipping targets completed mid-turn. Empty enteredDarts = server state.
        projectTurn(segments, currentSegment, enteredDarts) {
            if (!segments.length) return { index: 0, hits: 0, target: null }

            const startIndex = segments.findIndex((segment) =>
                this.isSameSegment(segment, currentSegment),
            )
            let index = startIndex === -1 ? 0 : startIndex
            let hits = segments[index]?.hits || 0

            const advancePastCompleted = () => {
                while (index < segments.length && hits >= this.hitsPerTarget) {
                    index++
                    hits = segments[index]?.hits || 0
                }
            }
            advancePastCompleted()

            for (const dart of enteredDarts) {
                if (index >= segments.length) break
                if (this.isHitOnSegment(dart, segments[index])) {
                    hits++
                    advancePastCompleted()
                }
            }

            return { index, hits, target: index < segments.length ? segments[index] : null }
        },
        isSameSegment(a, b) {
            return !!a && !!b && a.type === b.type && a.number === b.number
        },
        // Bulls carry a fixed score; every other segment hits its own number.
        hitNumberForSegment(segment) {
            if (!segment) return 0
            if (segment.type === DART_TYPES.BULL) return 50
            if (segment.type === DART_TYPES.OUTER_BULL) return 25
            return segment.number
        },
        isHitOnSegment(dart, segment) {
            return (
                dart.dart_type === segment.type &&
                dart.dart_number === this.hitNumberForSegment(segment)
            )
        },
        segmentLabel(segment) {
            if (!segment) return '—'
            if (segment.type === DART_TYPES.BULL || segment.type === DART_TYPES.OUTER_BULL) {
                return segment.type
            }
            return `${segment.type}${segment.number}`
        },
        addHit(option) {
            if (!option || this.enteredDarts.length >= this.dartsRemainingThisTurn) return
            this.enteredDarts.push({
                dart_type: option.dart_type,
                dart_number: option.dart_number,
            })
        },
        addMiss() {
            if (this.enteredDarts.length >= this.dartsRemainingThisTurn) return
            this.enteredDarts.push({
                dart_type: DART_TYPES.MISS,
                dart_number: 0,
            })
        },
        removeLastDart() {
            this.enteredDarts.pop()
        },
        async submitTurn() {
            const profileId = this.currentPlayer?.id
            if (!profileId || this.isSubmitting || this.enteredDarts.length === 0) return

            this.isSubmitting = true
            // Snapshot so a mid-loop turn change (and its watcher) can't mutate it.
            const darts = [...this.enteredDarts]
            try {
                for (let i = 0; i < darts.length; i++) {
                    // Each submitDart applies an authoritative server snapshot, so once
                    // a dart is confirmed it's already reflected in the server hits/darts
                    // counts. Drop it from enteredDarts immediately so the local preview
                    // (accuracy stats and projection in race mode) stops double-counting
                    // the darts the server has already accepted. A dart that fails to
                    // submit stays queued for retry.
                    await this.matchStore.submitDart({
                        profile_id: profileId,
                        dart_type: darts[i].dart_type,
                        dart_number: darts[i].dart_number,
                        dart_index: i,
                    })
                    this.enteredDarts.shift()
                }
            } catch (error) {
                console.error('[SEGMENTS] submit turn failed', error)
            } finally {
                this.isSubmitting = false
            }
        },
    },
}
</script>
